import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import useAuth from '../hooks/useAuth';

const OltDashboard = () => {
    const { user } = useAuth();
    const [olts, setOlts] = useState([]);
    const [oltSeleccionada, setOltSeleccionada] = useState(null);
    const [resumen, setResumen] = useState(null);
    const [puertos, setPuertos] = useState(null);
    const [loadingOlts, setLoadingOlts] = useState(true);
    const [loadingResumen, setLoadingResumen] = useState(false);
    const [loadingPuertos, setLoadingPuertos] = useState(false);
    const [error, setError] = useState('');
    const [vistaActiva, setVistaActiva] = useState('tarjetas');

    const loadOlts = useCallback(async () => {
        setLoadingOlts(true);
        try {
            const { data } = await api.get('/infraestructura/olt');
            const activas = data.filter(o => Number(o.activo));
            setOlts(activas);
            if (activas.length > 0) setOltSeleccionada(activas[0]);
        } catch {
            setError('Error cargando OLTs');
        } finally {
            setLoadingOlts(false);
        }
    }, []);

    useEffect(() => { if (user) loadOlts(); }, [loadOlts, user]);

    const cargarResumen = useCallback(async (olt) => {
        if (!olt) return;
        setLoadingResumen(true);
        setError('');
        setResumen(null);
        setPuertos(null);
        try {
            const { data } = await api.get(`/infraestructura/olt/${olt.id}/dashboard/resumen`);
            if (data.status === 'ok') setResumen(data);
            else setError(data.mensaje || 'Error conectando a la OLT');
        } catch (e) {
            setError(e.response?.data?.error || 'Error de conexión');
        } finally {
            setLoadingResumen(false);
        }
    }, []);

    const cargarPuertos = useCallback(async (olt) => {
        if (!olt) return;
        setLoadingPuertos(true);
        try {
            const { data } = await api.get(`/infraestructura/olt/${olt.id}/dashboard/puertos`);
            if (data.status === 'ok') setPuertos(data.puertos);
            else setError(data.mensaje || 'Error cargando puertos');
        } catch (e) {
            setError(e.response?.data?.error || 'Error cargando puertos');
        } finally {
            setLoadingPuertos(false);
        }
    }, []);

    useEffect(() => {
        if (oltSeleccionada) cargarResumen(oltSeleccionada);
    }, [oltSeleccionada, cargarResumen]);

    const KPI = ({ label, value, color, icon }) => (
        <div className={`bg-white rounded-xl p-4 shadow-sm border-l-4 ${color}`}>
            <p className="text-xs text-gray-500 uppercase font-medium">{icon} {label}</p>
            <p className="text-3xl font-bold text-gray-800 mt-1">{value ?? '—'}</p>
        </div>
    );

    const BarraRecurso = ({ label, valor }) => {
        const num = parseInt(valor) || 0;
        const barColor = num > 85 ? 'bg-red-500' : num > 60 ? 'bg-yellow-500' : 'bg-green-500';
        return (
            <div className="flex-1">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>{label}</span>
                    <span className="font-bold text-gray-700">{valor}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`h-2 rounded-full ${barColor}`} style={{ width: `${Math.min(num, 100)}%` }}></div>
                </div>
            </div>
        );
    };

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Dashboard OLT</h2>
                <div className="flex gap-3 items-center">
                    {loadingOlts ? (
                        <div className="text-gray-400 text-sm">Cargando OLTs...</div>
                    ) : (
                        <select value={oltSeleccionada?.id || ''} onChange={e => {
                            const olt = olts.find(o => String(o.id) === e.target.value);
                            setOltSeleccionada(olt);
                        }} className="border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none">
                            {olts.map(o => <option key={o.id} value={o.id}>{o.etiqueta}</option>)}
                        </select>
                    )}
                    <button onClick={() => cargarResumen(oltSeleccionada)}
                        disabled={loadingResumen}
                        className="text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60"
                        style={{ backgroundColor: 'var(--color-primary)' }}>
                        {loadingResumen ? '⏳ Cargando...' : '🔄 Actualizar'}
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>
            )}

            {/* Loading resumen */}
            {loadingResumen && (
                <div className="bg-white rounded-2xl p-12 text-center text-gray-400">
                    <div className="text-4xl mb-3">📡</div>
                    <p>Consultando OLT...</p>
                    <p className="text-xs mt-1">Esto toma unos segundos</p>
                </div>
            )}

            {/* Contenido principal */}
            {resumen && !loadingResumen && (
                <div className="space-y-4">

                    {/* Info equipo */}
                    <div className="bg-white rounded-2xl p-4 shadow-sm">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="font-bold text-gray-800 text-lg">{oltSeleccionada?.etiqueta}</p>
                                <p className="text-sm text-gray-500">{resumen.modelo} — {oltSeleccionada?.ip}:{oltSeleccionada?.puerto_nat || 2323}</p>
                                <p className="text-xs text-gray-400 mt-1">⏱ Uptime: {resumen.uptime}</p>
                                <p className="text-xs text-gray-400">🌡 Temperatura: {resumen.temperatura}</p>
                            </div>
                            <div className="flex gap-4 flex-1 max-w-md">
                                <BarraRecurso label="CPU" valor={resumen.cpu} />
                                <BarraRecurso label="Memoria" valor={resumen.memoria} />
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {resumen.slots?.map((s, i) => (
                                    <div key={i} className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-center">
                                        <p className="font-medium text-gray-700">Slot {s.slot}</p>
                                        <p className="text-gray-500">{s.board}</p>
                                        <p className={`font-medium ${
                                            s.status.includes('Active') ? 'text-green-600' :
                                            s.status.includes('Normal') ? 'text-blue-600' :
                                            'text-gray-500'
                                        }`}>{s.status.replace('_normal','').replace('_',' ')}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <KPI label="Autorizadas" value={resumen.total?.autorizadas} color="border-blue-500" icon="📋" />
                        <KPI label="En Línea"    value={resumen.total?.online}      color="border-green-500" icon="✅" />
                        <KPI label="Offline"     value={resumen.total?.offline}     color="border-red-500"   icon="❌" />
                        <KPI label="Dying Gasp"  value={resumen.total?.dying_gasp}  color="border-yellow-500" icon="⚠️" />
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-2 items-center">
                        <button onClick={() => setVistaActiva('tarjetas')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${vistaActiva === 'tarjetas' ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            style={vistaActiva === 'tarjetas' ? { backgroundColor: 'var(--color-primary)' } : {}}>
                            🖥️ Tarjetas
                        </button>
                        <button
                            onClick={() => {
                                setVistaActiva('puertos');
                                if (!puertos && !loadingPuertos) cargarPuertos(oltSeleccionada);
                            }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${vistaActiva === 'puertos' ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                            style={vistaActiva === 'puertos' ? { backgroundColor: 'var(--color-primary)' } : {}}>
                            {loadingPuertos ? '⏳ Cargando...' : '📡 Puertos PON'}
                        </button>
                    </div>

                    {/* Vista Tarjetas */}
                    {vistaActiva === 'tarjetas' && (
                        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                                    <tr>
                                        <th className="px-4 py-3 text-left">Slot</th>
                                        <th className="px-4 py-3 text-left">Tarjeta</th>
                                        <th className="px-4 py-3 text-left">Estado</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {resumen.slots?.map((s, i) => (
                                        <tr key={i} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 font-medium text-gray-800">{s.slot}</td>
                                            <td className="px-4 py-3 font-mono text-gray-600">{s.board}</td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                    s.status.includes('Active') ? 'bg-green-100 text-green-700' :
                                                    s.status.includes('Normal') ? 'bg-blue-100 text-blue-700' :
                                                    'bg-gray-100 text-gray-500'
                                                }`}>{s.status}</span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Vista Puertos */}
                    {vistaActiva === 'puertos' && (
                        <div>
                            {loadingPuertos && (
                                <div className="bg-white rounded-2xl p-8 text-center text-gray-400">
                                    <div className="text-3xl mb-2">📡</div>
                                    <p>Cargando puertos PON...</p>
                                </div>
                            )}
                            {puertos && !loadingPuertos && (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {puertos.map((p, i) => (
                                        <div key={i} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="font-bold text-gray-800">Puerto {p.puerto}</span>
                                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{p.total} ONTs</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <span className="flex-1 text-center bg-green-50 text-green-700 rounded-lg py-2">
                                                    <p className="text-xl font-bold">{p.online}</p>
                                                    <p className="text-xs">Online</p>
                                                </span>
                                                <span className="flex-1 text-center bg-red-50 text-red-600 rounded-lg py-2">
                                                    <p className="text-xl font-bold">{p.offline}</p>
                                                    <p className="text-xs">Offline</p>
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default OltDashboard;
